import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Send, Loader2, Clock, User, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PortalTicketDetail() {
  const navigate = useNavigate();
  const { ticket_id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const commentsEndRef = useRef(null);

  const getToken = () => localStorage.getItem('portal_token');
  const basePath = window.location.pathname.startsWith('/partner') ? '/partner' : '/portal';

  useEffect(() => {
    const token = localStorage.getItem('portal_token');
    if (!token) {
      navigate(basePath);
      return;
    }
    fetchTicket(token);
  }, [ticket_id, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTicket = async (token) => {
    try {
      if (!token) token = localStorage.getItem('portal_token');
      const res = await fetch(`${API}/api/portal/tickets/${ticket_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.status === 401) {
        navigate(basePath);
        return;
      }

      if (res.status === 404) {
        toast.error('Ticket not found');
        navigate(`${basePath}/dashboard`);
        return;
      }

      const data = await res.json();
      setTicket(data);
    } catch (error) {
      toast.error('Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleSendComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    setSending(true);
    try {
      const token = getToken();
      const res = await fetch(`${API}/api/portal/tickets/${ticket_id}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: comment })
      });

      if (res.ok) {
        setComment('');
        await fetchTicket();
        scrollToBottom();
        toast.success('Message sent');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-amber-100 text-amber-700',
      pending_customer: 'bg-purple-100 text-purple-700',
      resolved: 'bg-emerald-100 text-emerald-700',
      closed: 'bg-slate-100 text-slate-700'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{status?.replace('_', ' ')}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-slate-100 text-slate-600',
      medium: 'bg-blue-100 text-blue-600',
      high: 'bg-orange-100 text-orange-600',
      critical: 'bg-red-100 text-red-600'
    };
    return <Badge className={styles[priority] || 'bg-slate-100'}>{priority}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!ticket) return null;

  const isResolved = ['resolved', 'closed'].includes(ticket.status);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`${basePath}/dashboard`)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1 min-w-0">
              <p className="font-mono text-xs text-slate-400">{ticket.ticket_number}</p>
              <h1 className="font-bold text-base truncate">{ticket.subject}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2 ml-9">
            {getStatusBadge(ticket.status)}
            {getPriorityBadge(ticket.priority)}
          </div>
        </div>
      </header>

      {/* Ticket Info */}
      <div className="max-w-2xl mx-auto w-full p-4 space-y-4 flex-1 overflow-y-auto">
        <Card>
          <CardContent className="p-4 space-y-3">
            <div>
              <p className="text-xs text-slate-500 mb-1">Description</p>
              <p className="text-sm whitespace-pre-wrap">{ticket.description}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-3 border-t text-xs">
              <div>
                <p className="text-slate-500">Created</p>
                <p className="font-medium">
                  {new Date(ticket.created_at).toLocaleDateString('en-IN', {
                    day: 'numeric', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              </div>
              {ticket.assigned_to_name && (
                <div>
                  <p className="text-slate-500">Assigned To</p>
                  <p className="font-medium">{ticket.assigned_to_name}</p>
                </div>
              )}
              {ticket.circuit_code && (
                <div>
                  <p className="text-slate-500">Circuit</p>
                  <p className="font-medium font-mono">{ticket.circuit_code}</p>
                </div>
              )}
              {ticket.resolved_at && (
                <div>
                  <p className="text-slate-500">Resolved</p>
                  <p className="font-medium text-emerald-600">
                    {new Date(ticket.resolved_at).toLocaleDateString('en-IN', {
                      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                    })}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Conversation */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Conversation</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {ticket.comments?.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">
                No messages yet
              </div>
            ) : (
              <div className="divide-y max-h-[400px] overflow-y-auto">
                {ticket.comments?.map((c) => (
                  <div 
                    key={c.comment_id} 
                    className={`p-4 ${c.author_type === 'customer' ? 'bg-emerald-50' : 'bg-white'}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                        c.author_type === 'customer' ? 'bg-emerald-200 text-emerald-700' : 'bg-blue-200 text-blue-700'
                      }`}>
                        {c.author_name?.charAt(0) || 'U'}
                      </div>
                      <span className="font-medium text-sm">{c.author_name}</span>
                      <span className="text-xs text-slate-400">
                        {new Date(c.created_at).toLocaleTimeString('en-IN', {
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 ml-8 whitespace-pre-wrap">{c.content}</p>
                  </div>
                ))}
                <div ref={commentsEndRef} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Resolution Notice */}
        {isResolved && (
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              <div>
                <p className="font-medium text-emerald-800">Ticket {ticket.status}</p>
                <p className="text-xs text-emerald-600">
                  If you need further assistance, please raise a new ticket
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Message Input */}
      {!isResolved && (
        <div className="sticky bottom-0 bg-white border-t p-4">
          <form onSubmit={handleSendComment} className="max-w-2xl mx-auto flex gap-2">
            <Input
              placeholder="Type your message..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="flex-1"
            />
            <Button type="submit" disabled={sending || !comment.trim()}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}
